// ==========================================
// FILE: frontend/src/components/FeedbackForm.jsx
// ==========================================
import React, { useState, useEffect } from 'react';
import { Plus, X, Upload, Trash2, Link, Save, Loader, AlertCircle, CheckCircle, Quote, FileText } from 'lucide-react';
import { apiService } from '../../services/api.service'; 
import { ROUTES, STORAGE_KEYS } from '../../config/api.config'; 
import logo from '../../assests/images/logo1.png'; 

// Branding Quotes
const QUOTES = [
  "GLOCYBS: Pioneering Quantum-Resilient Cybersecurity for a Boundless Future.",
  "BIQUA: Securing the Digital Frontier with Next-Gen Cryptography.",
  "Empowering Trust in the Quantum Era with GLOCYBS & BIQUA.",
  "Redefining Digital Sovereignty through Advanced Innovation."
];

const FeedbackForm = () => {
  // State
  const [columns, setColumns] = useState([]);
  const [showColumnInput, setShowColumnInput] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  
  const [formData, setFormData] = useState({});
  const [links, setLinks] = useState({});
  const [images, setImages] = useState({}); 

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [quoteIndex, setQuoteIndex] = useState(0);
  
  const [currentUser, setCurrentUser] = useState(null);

  // Cycle through quotes
  useEffect(() => {
    const interval = setInterval(() => {
      setQuoteIndex((prev) => (prev + 1) % QUOTES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Auth Check
// Make Feedback Form PUBLIC
useEffect(() => {
  const init = () => {

    // 🔥 If not authenticated → allow user to submit anonymously
    if (!apiService.isAuthenticated()) {
      setCurrentUser({ full_name: "Guest User", username: "guest" });
      setLoading(false);
      return;
    }

    // Logged-in user logic
    try {
      const userDataString = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      if (userDataString) {
        const user = JSON.parse(userDataString);
        setCurrentUser(user);
      } else {
        setCurrentUser({ full_name: "Guest User", username: "guest" });
      }
    } catch (e) {
      console.error("Error parsing user data", e);
      setCurrentUser({ full_name: "Guest User", username: "guest" });
    } finally {
      setLoading(false);
    }
  };

  init();
}, []);

  const addColumn = () => {
    if (newColumnName.trim()) {
      const columnId = `col_${Date.now()}`;
      setColumns([...columns, { id: columnId, name: newColumnName }]);
      setFormData({ ...formData, [columnId]: '' });
      setLinks({ ...links, [columnId]: '' });
      setImages({ ...images, [columnId]: [] });
      setNewColumnName('');
      setShowColumnInput(false);
    }
  };

  const removeColumn = (columnId) => {
    setColumns(columns.filter(col => col.id !== columnId));
    const newFormData = { ...formData };
    const newLinks = { ...links };
    const newImages = { ...images };
    delete newFormData[columnId];
    delete newLinks[columnId];
    delete newImages[columnId];
    setFormData(newFormData);
    setLinks(newLinks);
    setImages(newImages);
  };

  const handleInputChange = (columnId, value) => setFormData({ ...formData, [columnId]: value });
  const handleLinkChange = (columnId, value) => setLinks({ ...links, [columnId]: value });

  const handleFileSelect = (columnId, fileList) => {
    const fileArray = Array.from(fileList);
    const currentFiles = images[columnId] || [];
    
    const newFiles = fileArray.map(file => ({
      file,
      preview: URL.createObjectURL(file), 
      id: `file_${Date.now()}_${Math.random()}`,
      type: file.type
    }));
    
    setImages({ ...images, [columnId]: [...currentFiles, ...newFiles] });
  };

  const removeFile = (columnId, fileId) => {
    const updatedFiles = images[columnId].filter(f => f.id !== fileId);
    setImages({ ...images, [columnId]: updatedFiles });
  };

  const handleSubmit = async () => {
    if (!currentUser) {
      setError('User session invalid. Please reload.');
      return;
    }

    if (columns.length === 0) {
      setError('Please add at least one issue or topic.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const fieldsWithFileUrls = await Promise.all(
        columns.map(async (column) => {
          let fileUrls = [];
          if (images[column.id] && images[column.id].length > 0) {
            const formDataUpload = new FormData();
            formDataUpload.append('field_id', column.id);
            images[column.id].forEach(fileObj => {
              formDataUpload.append('images', fileObj.file); 
            });

            const uploadResponse = await apiService.upload('/submissions/upload', formDataUpload);
            if (uploadResponse.success && uploadResponse.data?.image_urls) {
              fileUrls = uploadResponse.data.image_urls;
            }
          }

          return {
            id: column.id,
            name: column.name,
            text: formData[column.id] || '',
            link: links[column.id] || '',
            images: fileUrls 
          };
        })
      );

      const payload = {
        username: currentUser.username || currentUser.email,
        fields: fieldsWithFileUrls
      };

      const response = await apiService.post('/submissions', payload);

      if (response.success) {
        setSuccess('Feedback submitted successfully! Thank you.');
        setColumns([]);
        setFormData({});
        setLinks({});
        setImages({});
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(response.error || 'Submission failed');
      }
    } catch (err) {
      setError(err.message || 'Connection error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
     return (
        <div className="h-screen flex items-center justify-center bg-gray-50">
            <Loader className="animate-spin text-purple-600" size={40} />
        </div>
     );
  }

  return (
    // UPDATED: h-screen and overflow-hidden ensures the whole page doesn't scroll
    // UPDATED: pt-4 moves the whole container up (was pt-24)
    <div className="h-screen w-full bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 pt-20 pb-4 px-4 md:px-8 font-sans relative overflow-hidden flex flex-col items-center">
      
      {/* Decorative Background */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-72 h-72 bg-cyan-400/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-violet-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="w-full max-w-5xl h-full relative z-10">
        
        {/* UPDATED: h-full and flex flex-col to enable internal scrolling */}
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/50 h-full flex flex-col overflow-hidden">
          
          {/* ================================================= */}
          {/* FIXED HEADER SECTION (Does not scroll)            */}
          {/* ================================================= */}
          <div className="flex-shrink-0 p-6 sm:p-8 border-b border-gray-100 bg-white/50 backdrop-blur-md z-20">
            <div className="flex flex-col items-center justify-center gap-2 mb-2">
              {/* Logo */}
              <div className="p-2 bg-white rounded-2xl shadow-md">
                 <img src={logo} alt="GLOCYBS Logo" className="h-16 w-auto object-contain" />
              </div>
              
              <div className="text-center">
                <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-gray-800 via-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Feedback Console
                </h1>
                <p className="text-gray-500 font-semibold tracking-wide text-xs uppercase mt-1">
                  Powered by <span className="text-cyan-600">GLOCYBS</span> & <span className="text-purple-600">BIQUA</span>
                </p>
              </div>
            </div>

            {/* Quote */}
            <div className="mt-4 max-w-xl mx-auto bg-blue-50/50 rounded-lg p-2 border border-blue-100 flex items-center gap-3">
              <Quote size={16} className="text-blue-400 flex-shrink-0" />
              <p className="text-gray-600 text-xs font-medium italic text-center w-full truncate">
                {QUOTES[quoteIndex]}
              </p>
            </div>

             {/* User Welcome */}
            <div className="text-center mt-2">
                <p className="text-gray-500 text-sm">
                    Reporting as <span className="font-bold text-purple-600">{currentUser?.full_name || currentUser?.username}</span>
                </p>
            </div>

            {/* Status Messages */}
            {(error || success) && (
              <div className="mt-2 flex justify-center w-full">
                {error && (
                    <div className="p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 animate-shake">
                    <AlertCircle className="text-red-600" size={16} />
                    <p className="text-red-700 text-sm">{error}</p>
                    </div>
                )}
                {success && (
                    <div className="p-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                    <CheckCircle className="text-green-600" size={16} />
                    <p className="text-green-700 text-sm">{success}</p>
                    </div>
                )}
              </div>
            )}
          </div>

          {/* ================================================= */}
          {/* FIXED ADD BUTTON / INPUT SECTION (Does not scroll)*/}
          {/* ================================================= */}
          <div className="flex-shrink-0 p-4 bg-white/60 border-b border-gray-200 shadow-sm z-10">
            {!showColumnInput ? (
              <button
                onClick={() => setShowColumnInput(true)}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-xl transition-all shadow-md hover:shadow-cyan-500/30 font-bold text-lg"
              >
                <Plus size={20} strokeWidth={2.5} />
                <span>Add New Issue</span>
              </button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addColumn()}
                  placeholder="Enter issue (e.g., 'GLOCYBS Login Error')..."
                  className="flex-1 px-4 py-3 bg-white border-2 border-gray-300 rounded-xl focus:border-cyan-600 focus:ring-4 focus:ring-cyan-100 focus:outline-none text-gray-900 font-medium"
                  autoFocus
                />
                <button 
                  onClick={addColumn} 
                  className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white rounded-xl font-bold shadow-lg flex-shrink-0"
                >
                  Add
                </button>
                <button 
                  onClick={() => setShowColumnInput(false)} 
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold flex-shrink-0"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* ================================================= */}
          {/* SCROLLABLE AREA (Only this part scrolls now)      */}
          {/* ================================================= */}
          <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6 bg-transparent">
            
            {/* List of Feedback Items */}
            {columns.map((column, index) => (
              <div 
                key={column.id} 
                className="bg-white/50 backdrop-blur-sm p-6 rounded-2xl border border-gray-200 hover:border-cyan-400 transition-all shadow-sm"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 text-white flex items-center justify-center font-bold text-sm shadow-md">
                      {index + 1}
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">{column.name}</h3>
                  </div>
                  <button 
                    onClick={() => removeColumn(column.id)} 
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  >
                    <X size={20} />
                  </button>
                </div>

                <textarea
                  rows={2}
                  value={formData[column.id] || ''}
                  onChange={(e) => handleInputChange(column.id, e.target.value)}
                  placeholder={`Describe the issue...`}
                  className="w-full px-4 py-3 mb-3 bg-white border border-gray-200 rounded-xl focus:border-violet-500 focus:ring-2 focus:ring-violet-100 focus:outline-none text-gray-900 resize-none font-medium text-sm"
                />

                <div className="relative mb-4 group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Link size={16} className="text-gray-400 group-focus-within:text-cyan-600" />
                  </div>
                  <input
                    type="url"
                    value={links[column.id] || ''}
                    onChange={(e) => handleLinkChange(column.id, e.target.value)}
                    placeholder="Add reference URL (Optional)"
                    className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg focus:border-cyan-600 focus:ring-2 focus:ring-cyan-100 focus:outline-none text-gray-700 text-sm"
                  />
                </div>

                <div className="space-y-4">
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:border-cyan-400 text-gray-700 rounded-lg cursor-pointer font-semibold shadow-sm hover:shadow-md transition-all text-sm">
                    <Upload size={16} />
                    <span>Upload Evidence</span>
                    <input 
                      type="file" 
                      multiple 
                      accept="image/*,application/pdf" 
                      onChange={(e) => handleFileSelect(column.id, e.target.files)} 
                      className="hidden" 
                    />
                  </label>

                  {/* Previews */}
                  {images[column.id] && images[column.id].length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {images[column.id].map((fileObj) => (
                        <div key={fileObj.id} className="relative group/img aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                          {fileObj.file.type === 'application/pdf' ? (
                            <div className="w-full h-full flex flex-col items-center justify-center p-1 text-gray-500">
                                <FileText size={24} className="text-red-500 mb-1" />
                                <span className="text-[10px] text-center font-medium leading-tight line-clamp-2">
                                  {fileObj.file.name}
                                </span>
                            </div>
                          ) : (
                            <img 
                              src={fileObj.preview} 
                              alt="Preview" 
                              className="w-full h-full object-cover" 
                            />
                          )}
                          <button 
                            onClick={() => removeFile(column.id, fileObj.id)} 
                            className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full opacity-0 group-hover/img:opacity-100 transition-all shadow-md"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Submit Section */}
            {columns.length > 0 && (
              <div className="pt-4 pb-8">
                <button 
                  onClick={handleSubmit} 
                  disabled={submitting}
                  className="w-full py-4 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 hover:from-violet-500 hover:via-purple-500 hover:to-fuchsia-500 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-3 shadow-xl shadow-violet-500/30 transition-all transform hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader className="animate-spin" size={24} />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Save size={24} />
                      <span>Submit Feedback</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Empty State */}
            {columns.length === 0 && (
              <div className="text-center py-10 opacity-70">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gray-100 text-gray-400 mb-3">
                  <Plus size={40} strokeWidth={1.5} />
                </div>
                <p className="text-gray-500 text-base font-medium">No issues added yet</p>
                <p className="text-gray-400 text-xs mt-1">Add a bug report for <span className="font-bold">GLOCYBS/BIQUA</span>.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;